import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import axios from "axios";
import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-hot-toast";
import {
  FiUsers,
  FiChevronsLeft,
  FiChevronsRight,
  FiChevronLeft,
  FiChevronRight,
  FiRefreshCw,
  FiX,
  FiPlus,
  FiSearch,
  FiDownload,
} from "react-icons/fi";
import {
  fetchConstituencies,
  fetchAllConstituencies,
  createConstituency,
} from "../../redux/actions/user-management/constituencyActions";
import {
  fetchDistricts,
  fetchMunicipalities,
  clearMunicipalities,
} from "../../redux/actions/internal/internalActions";

const Constituency = () => {
  const dispatch = useDispatch();
  const { loading, constituencies, pagination } = useSelector(
    (state) =>
      state.constituency || {
        loading: false,
        constituencies: [],
        pagination: { page: 1, limit: 10, totalRecords: 0, totalPages: 0 },
      },
  );

  const { districts, municipalities } = useSelector(
    (state) => state.internal || { districts: [], municipalities: [] },
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const limitOptions = [
    10, 20, 50, 100, 200, 500, 1000, 1500, 2000, 3000, 4000,
  ];
  const [filters, setFilters] = useState({
    district_id: "",
    municipality_id: "",
    search: "",
  });

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    district_id: "",
    municipality_id: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    dispatch(fetchConstituencies(currentPage, limit, filters));
    dispatch(fetchDistricts());
  }, [
    dispatch,
    currentPage,
    limit,
    filters.district_id,
    filters.municipality_id,
    filters.search,
  ]);

  useEffect(() => {
    if (filters.district_id) {
      dispatch(fetchMunicipalities(filters.district_id));
    } else {
      dispatch(clearMunicipalities());
    }
  }, [filters.district_id, dispatch]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
      dispatch(fetchConstituencies(page, limit, filters));
    }
  };

  const handleRefresh = () => {
    dispatch(fetchConstituencies(currentPage, limit, filters));
    toast.success("Data refreshed!");
  };

  // Export to Excel (current page)
  const handleExportToExcel = async () => {
    if (constituencies.length === 0) {
      toast.error("No data to export");
      return;
    }

    const headers = ["ID", "Name", "District", "Municipality", "Date"];

    const rows = constituencies.map((item) => [
      { value: item.id || "N/A", changed: false },
      { value: item.name || "N/A", changed: false },
      { value: item.district_name || "N/A", changed: false },
      { value: item.municipality_name || "N/A", changed: false },
      {
        value: item.updated_at
          ? new Date(item.updated_at).toLocaleDateString("en-IN")
          : "N/A",
        changed: false,
      },
    ]);

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Constituencies");

    // Header
    const headerRow = worksheet.addRow(headers.map((h) => h));
    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFFB923C" },
    };
    headerRow.alignment = { horizontal: "center", vertical: "middle" };

    // Data rows (no change coloring)
    rows.forEach((rowData) => {
      const row = worksheet.addRow(rowData.map((cell) => cell.value));
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin", color: { argb: "FFD3D3D3" } },
          left: { style: "thin", color: { argb: "FFD3D3D3" } },
          bottom: { style: "thin", color: { argb: "FFD3D3D3" } },
          right: { style: "thin", color: { argb: "FFD3D3D3" } },
        };
        cell.alignment = {
          horizontal: "center",
          vertical: "middle",
          wrapText: true,
        };
      });
    });

    worksheet.columns = [
      { width: 8 },
      { width: 30 },
      { width: 25 },
      { width: 25 },
      { width: 18 },
    ];

    worksheet.views = [{ state: "frozen", ySplit: 1 }];

    const buffer = await workbook.xlsx.writeBuffer();
    const date = new Date();
    const dateStr = `${String(date.getDate()).padStart(2, "0")}-${String(
      date.getMonth() + 1,
    ).padStart(2, "0")}-${date.getFullYear()}`;
    saveAs(new Blob([buffer]), `Constituencies_${dateStr}.xlsx`);
    toast.success("Export successful!");
  };

  // Bulk Export All
  const handleBulkExportToExcel = async () => {
    if (exporting) return; // Prevent multiple clicks

    setExporting(true);

    try {
      toast.loading("Fetching all records...", { id: "bulk-export" });

      const allData = await fetchAllConstituencies(filters, 2000000);

      if (!allData || allData.length === 0) {
        toast.dismiss("bulk-export");
        toast.error("No data to export");
        setExporting(false);
        return;
      }

      const headers = ["ID", "Name", "District", "Municipality", "Date"];

      const rows = allData.map((item, index) => [
        // { value: index + 1, changed: false },
        { value: item.id || "N/A", changed: false },
        { value: item.name || "N/A", changed: false },
        { value: item.district_name || "N/A", changed: false },
        { value: item.municipality_name || "N/A", changed: false },

        {
          value: item.updated_at
            ? new Date(item.updated_at).toLocaleDateString("en-IN")
            : "N/A",
          changed: false,
        },
      ]);

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Constituencies");

      const headerRow = worksheet.addRow(headers);
      headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
      headerRow.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFB923C" },
      };
      headerRow.alignment = { horizontal: "center", vertical: "middle" };

      rows.forEach((rowData) => {
        const row = worksheet.addRow(rowData.map((cell) => cell.value));
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin", color: { argb: "FFD3D3D3" } },
            left: { style: "thin", color: { argb: "FFD3D3D3" } },
            bottom: { style: "thin", color: { argb: "FFD3D3D3" } },
            right: { style: "thin", color: { argb: "FFD3D3D3" } },
          };
          cell.alignment = {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          };
        });
      });

      worksheet.columns = [
        { width: 8 },
        { width: 30 },
        { width: 25 },
        { width: 25 },
        { width: 18 },
      ];
      worksheet.views = [{ state: "frozen", ySplit: 1 }];

      const buffer = await workbook.xlsx.writeBuffer();
      const date = new Date();
      const dateStr = `${String(date.getDate()).padStart(2, "0")}-${String(
        date.getMonth() + 1,
      ).padStart(2, "0")}-${date.getFullYear()}`;

      saveAs(new Blob([buffer]), `Constituencies_All_${dateStr}.xlsx`);
      toast.success(`Exported ${allData.length} records successfully!`, {
        id: "bulk-export",
      });
    } catch (error) {
      toast.error("Failed to export", { id: "bulk-export" });
      console.error("Bulk export error:", error);
    } finally {
      setExporting(false); // Re-enable button
    }
  }; // Modal handlers
  const openAddModal = () => {
    setFormData({ id: null, name: "", district_id: "", municipality_id: "" });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setFormData({ id: null, name: "", district_id: "", municipality_id: "" });
    setSubmitting(false);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // If district changes, clear municipality and fetch new list
    if (name === "district_id") {
      setFormData((prev) => ({ ...prev, municipality_id: "" }));
      if (value) {
        dispatch(fetchMunicipalities(value));
      } else {
        dispatch(clearMunicipalities());
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await dispatch(
        createConstituency({
          name: formData.name,
          district_id: formData.district_id,
          municipality_id: formData.municipality_id,
        }),
      );
      dispatch(fetchConstituencies(currentPage, limit, filters));
      closeModal();
    } catch (error) {
      console.error("Submit error:", error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-2 md:p-4 max-w-full mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold uppercase text-gray-900 dark:text-white">
            Constituencies
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-1">
            Manage assembly constituencies
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={handleBulkExportToExcel}
            disabled={exporting}
            className={`flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors ${
              exporting ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <FiDownload className="w-4 h-4" />
            {exporting ? "Exporting..." : "Export All Records"}
          </button>
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors"
          >
            <FiRefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={handleExportToExcel}
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
          >
            <FiDownload className="w-4 h-4" />
            Export to Excel
          </button>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            Add Constituency
          </button>
        </div>
      </div>

      {/* Table Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <FiUsers className="w-5 h-5 text-gray-800 dark:text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Constituency List
            </h2>
            <span className="text-sm font-medium text-orange-800 dark:text-orange-200 border border-orange-200 dark:border-orange-700 px-3 py-1 rounded-md">
              TOTAL: {pagination.totalRecords || 0}
            </span>
            <div className="flex items-center gap-2 ml-4">
              <label className="text-sm text-gray-800 dark:text-gray-600">
                Show:
              </label>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-1 border-2 border-orange-200 dark:border-orange-700 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-medium min-w-[80px]"
              >
                {limitOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* First Row - District and Municipality */}
          <div className="flex items-center gap-3 mb-3">
            <select
              value={filters.district_id}
              onChange={(e) => {
                const value = e.target.value;
                setFilters({
                  district_id: value,
                  municipality_id: "",
                  search: filters.search,
                });
                setCurrentPage(1);
              }}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm min-w-[180px]"
            >
              <option value="">All Districts</option>
              {districts.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>

            <select
              value={filters.municipality_id}
              onChange={(e) => {
                setFilters({
                  ...filters,
                  municipality_id: e.target.value,
                });
                setCurrentPage(1);
              }}
              disabled={!filters.district_id}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm min-w-[180px] disabled:opacity-50"
            >
              <option value="">All Municipalities</option>
              {municipalities.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </div>

          {/* Second Row - Search and Clear Button */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by name"
                value={filters.search}
                onChange={(e) => {
                  setFilters({
                    ...filters,
                    search: e.target.value,
                  });
                  setCurrentPage(1);
                }}
                className="px-4 py-2 pl-9 border-2 border-orange-400 dark:border-orange-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm w-full min-w-[450px] transition-all duration-200"
              />
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
                <FiSearch className="w-4 h-4" />
              </span>
            </div>

            {(filters.district_id ||
              filters.municipality_id ||
              filters.search) && (
              <button
                onClick={() => {
                  setFilters({
                    district_id: "",
                    municipality_id: "",
                    search: "",
                  });
                  setCurrentPage(1);
                  dispatch(clearMunicipalities());
                }}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg transition-colors text-sm flex items-center gap-1"
              >
                <FiX className="w-4 h-4" />
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Pagination Top */}
        {pagination.totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 pt-4 pb-4 border-t border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Showing{" "}
              <span className="font-semibold text-gray-800 dark:text-gray-200">
                {(pagination.page - 1) * pagination.limit + 1}
              </span>{" "}
              to{" "}
              <span className="font-semibold text-gray-800 dark:text-gray-200">
                {Math.min(
                  pagination.page * pagination.limit,
                  pagination.totalRecords,
                )}
              </span>{" "}
              of{" "}
              <span className="font-semibold text-gray-800 dark:text-gray-200">
                {pagination.totalRecords}
              </span>{" "}
              entries
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
              >
                <FiChevronsLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
              >
                <FiChevronLeft className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-1.5">
                {(() => {
                  const totalPages = pagination.totalPages;
                  const current = currentPage;
                  const pages = [];
                  const maxVisible = 5;

                  if (totalPages <= maxVisible) {
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    if (current <= 3) {
                      pages.push(1, 2, 3, 4, "...", totalPages);
                    } else if (current >= totalPages - 2) {
                      pages.push(
                        1,
                        "...",
                        totalPages - 3,
                        totalPages - 2,
                        totalPages - 1,
                        totalPages,
                      );
                    } else {
                      pages.push(
                        1,
                        "...",
                        current - 1,
                        current,
                        current + 1,
                        "...",
                        totalPages,
                      );
                    }
                  }

                  return pages.map((page, index) =>
                    page === "..." ? (
                      <span
                        key={`ellipsis-${index}`}
                        className="px-2 text-gray-500 dark:text-gray-400"
                      >
                        …
                      </span>
                    ) : (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                          current === page
                            ? "bg-orange-600 text-white shadow-md shadow-orange-500/30"
                            : "border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                        }`}
                      >
                        {page}
                      </button>
                    ),
                  );
                })()}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === pagination.totalPages}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
              >
                <FiChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => handlePageChange(pagination.totalPages)}
                disabled={currentPage === pagination.totalPages}
                className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
              >
                <FiChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-10 h-10 border-4 border-orange-600 dark:border-orange-400 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : constituencies.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700/30">
                    <th className="py-1.5 px-2 text-center text-xs font-medium border border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 uppercase">
                      ID
                    </th>
                    <th className="py-1.5 px-2 text-center text-xs font-medium border border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 uppercase min-w-[150px]">
                      Name
                    </th>
                    <th className="py-1.5 px-2 text-center text-xs font-medium border border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 uppercase min-w-[150px]">
                      District
                    </th>
                    <th className="py-1.5 px-2 text-center text-xs font-medium border border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 uppercase min-w-[150px]">
                      Municipality
                    </th>
                    <th className="py-1.5 px-2 text-center text-xs font-medium border border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 uppercase min-w-[130px]">
                      Created At
                    </th>
                    <th className="py-1.5 px-2 text-center text-xs font-medium border border-gray-300 dark:border-gray-600 text-orange-800 dark:text-orange-400 uppercase min-w-[130px]">
                      Updated At
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {constituencies.map((item) => (
                    <tr
                      key={item.id}
                      className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <td className="py-1.5 px-2 text-center text-xs text-gray-500 dark:text-gray-600 border border-gray-300 dark:border-gray-600">
                        {item.id}
                      </td>
                      <td className="py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                        {item.name}
                      </td>
                      <td className="py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                        {item.district_name || "N/A"}
                      </td>
                      <td className="py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                        {item.municipality_name || "N/A"}
                      </td>
                      <td className="py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                        {item.created_at
                          ? new Date(item.created_at).toLocaleDateString(
                              "en-IN",
                            )
                          : "N/A"}
                      </td>
                      <td className="py-1.5 px-2 text-center text-xs text-gray-800 dark:text-gray-300 border border-gray-300 dark:border-gray-600">
                        {item.updated_at
                          ? new Date(item.updated_at).toLocaleDateString(
                              "en-IN",
                            )
                          : "N/A"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Bottom */}
            {pagination.totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Showing{" "}
                  <span className="font-semibold text-gray-800 dark:text-gray-200">
                    {(pagination.page - 1) * pagination.limit + 1}
                  </span>{" "}
                  to{" "}
                  <span className="font-semibold text-gray-800 dark:text-gray-200">
                    {Math.min(
                      pagination.page * pagination.limit,
                      pagination.totalRecords,
                    )}
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold text-gray-800 dark:text-gray-200">
                    {pagination.totalRecords}
                  </span>{" "}
                  entries
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
                  >
                    <FiChevronsLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
                  >
                    <FiChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="flex items-center gap-1.5">
                    {(() => {
                      const totalPages = pagination.totalPages;
                      const current = currentPage;
                      const pages = [];
                      const maxVisible = 5;

                      if (totalPages <= maxVisible) {
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        if (current <= 3) {
                          pages.push(1, 2, 3, 4, "...", totalPages);
                        } else if (current >= totalPages - 2) {
                          pages.push(
                            1,
                            "...",
                            totalPages - 3,
                            totalPages - 2,
                            totalPages - 1,
                            totalPages,
                          );
                        } else {
                          pages.push(
                            1,
                            "...",
                            current - 1,
                            current,
                            current + 1,
                            "...",
                            totalPages,
                          );
                        }
                      }

                      return pages.map((page, index) =>
                        page === "..." ? (
                          <span
                            key={`ellipsis-${index}`}
                            className="px-2 text-gray-500 dark:text-gray-400"
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                              current === page
                                ? "bg-orange-600 text-white shadow-md shadow-orange-500/30"
                                : "border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
                            }`}
                          >
                            {page}
                          </button>
                        ),
                      );
                    })()}
                  </div>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === pagination.totalPages}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
                  >
                    <FiChevronRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handlePageChange(pagination.totalPages)}
                    disabled={currentPage === pagination.totalPages}
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200 text-gray-600 dark:text-gray-400"
                  >
                    <FiChevronsRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <FiUsers className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-600">
              No constituencies found
            </p>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={closeModal}
        >
          <div
            className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <FiX className="w-6 h-6" />
            </button>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              Add Constituency
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    District *
                  </label>
                  <select
                    name="district_id"
                    value={formData.district_id}
                    onChange={handleFormChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">Select District</option>
                    {districts.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Municipality *
                  </label>
                  <select
                    name="municipality_id"
                    value={formData.municipality_id}
                    onChange={handleFormChange}
                    required
                    disabled={!formData.district_id}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50"
                  >
                    <option value="">Select Municipality</option>
                    {municipalities.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {submitting ? "Saving..." : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Constituency;
